import * as Popover from '@radix-ui/react-popover'
import * as Slider from '@radix-ui/react-slider'
import * as Switch from '@radix-ui/react-switch'
import { type icons } from 'lucide-react'
import React, { useId, useState } from 'react'
import { ColorPicker } from '#app/components/tiptap/panels/index.js'
import { Surface } from '#app/components/tiptap/ui/Surface.js'
import { Button } from './ui/button.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map(e => (
				<li
					key={e}
					className="text-sm leading-none text-foreground-destructive"
				>
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
	label = true,
	variant = 'default',
	icon,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
	label?: boolean
	variant?: 'default' | 's'
	icon?: keyof typeof icons | null
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={[className, 'flex flex-col gap-3'].join(' ')}>
			{label ? (
				<div className="flex items-center justify-between">
					<Label htmlFor={id} {...labelProps} />
					{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
				</div>
			) : null}
			<Input
				id={id}
				icon={icon}
				variant={variant}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={[className, 'flex flex-col gap-3'].join(' ')}>
			<div className="flex items-center justify-between">
				<Label htmlFor={id} {...labelProps} />
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
		</div>
	)
}

export function SliderField({
	labelProps,
	inputProps,
	sliderProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	sliderProps: Slider.SliderProps
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={[className, 'flex flex-col gap-2'].join(' ')}>
			<input id={id} hidden {...inputProps} />
			<Slider.Root
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				className="relative flex h-[20px] w-full touch-none select-none items-center"
				{...sliderProps}
			>
				<Slider.Track className="relative h-[3px] flex-grow rounded-full bg-primary">
					<Slider.Range className="absolute h-full rounded-full bg-secondary" />
				</Slider.Track>
				<Slider.Thumb
					className="group flex h-[20px] w-[20px] flex-col items-center justify-center overflow-hidden rounded-[10px] border border-border bg-primary-foreground text-xs font-medium text-primary shadow transition-all duration-150 ease-in hover:w-[50px] hover:bg-primary focus:outline-none"
					aria-label="Volume"
				>
					<span className="px-2 text-secondary opacity-0 transition-all duration-150 ease-in group-hover:opacity-100">
						{inputProps.value}px
					</span>
				</Slider.Thumb>
			</Slider.Root>
		</div>
	)
}

// export function SelectField({
// 	labelProps,
// 	inputProps,
// 	errors,
// 	className,
// 	label = true,
// 	items,
// }: {
// 	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
// 	inputProps: React.InputHTMLAttributes<HTMLInputElement>
// 	errors?: ListOfErrors
// 	className?: string
// 	label?: boolean
// 	items: string[]
// }) {
// 	const fallbackId = useId()
// 	const id = inputProps.id ?? fallbackId
// 	const errorId = errors?.length ? `${id}-error` : undefined
// 	return (
// 		<div className={[className, 'flex flex-col gap-3'].join(' ')}>
// 			{label ? (
// 				<div className="flex items-center justify-between">
// 					<Label htmlFor={id} {...labelProps} />
// 					{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
// 				</div>
// 			) : null}
// 			<Select
// 				id={id}
// 				items={items}
// 				aria-invalid={errorId ? true : undefined}
// 				aria-describedby={errorId}
// 				{...inputProps}
// 			/>
// 		</div>
// 	)
// }

export function ColorField({
	labelProps,
	inputProps,
	tooltip = '',
	errors,
	className,
	label = true,
	defaultColor = '#000000',
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
	label?: boolean
	tooltip?: string
	defaultColor?: string
}) {
	const fallbackId = useId()
	const id = inputProps?.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const [color, setColor] = useState(defaultColor)

	// useEffect(() => {
	// 	setColor(defaultColor)
	// }, [defaultColor])

	return (
		<div className={[className, 'color-field flex flex-col gap-3'].join(' ')}>
			{label ? (
				<div className="flex items-center justify-between">
					<Label htmlFor={id} {...labelProps} />
					{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
				</div>
			) : null}
			<input id={id} value={color} {...inputProps} hidden />
			<Popover.Root>
				<Popover.Trigger asChild>
					<div
						data-tooltip={tooltip}
						data-tooltip-direction="up"
						style={{ backgroundColor: color }}
						className="h-8 w-8 cursor-pointer rounded-full border border-border shadow-button hover:pb-[1px] active:pt-1 active:shadow-none"
					></div>
				</Popover.Trigger>
				<Popover.Content side="top" sideOffset={8} asChild>
					<Surface className="p-1">
						<ColorPicker
							color={color}
							onChange={(newColor: string) => {
								setColor(newColor)
								inputProps?.onChange?.({
									target: { value: newColor },
								} as React.ChangeEvent<HTMLInputElement>)
							}}
						/>
					</Surface>
				</Popover.Content>
			</Popover.Root>
		</div>
	)
}

export const SimpleColorPicker = ({
	color = '#ffffff',
	tooltip,
	onChange,
}: {
	color: string
	tooltip?: string
	onChange: ((color: string) => void) | undefined
}) => {
	return (
		<Popover.Root>
			<Popover.Trigger asChild>
				<div
					data-tooltip={tooltip}
					data-tooltip-direction="up"
					style={{ backgroundColor: color }}
					className="h-8 w-8 cursor-pointer rounded-full border border-border shadow-button hover:pb-[1px] active:pt-1 active:shadow-none"
				></div>
			</Popover.Trigger>
			<Popover.Content
				asChild
				sideOffset={12}
				side="top"
				align="center"
				alignOffset={0}
				className="custom-popover z-[8]"
			>
				<Surface>
					<ColorPicker color={color} onChange={onChange} />
				</Surface>
			</Popover.Content>
		</Popover.Root>
	)
}

export function UploadField({
	labelProps,
	inputProps,
	errors,
	className,
	label = true,
	newImageSrc,
	setNewImageSrc,
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
	label?: boolean
	newImageSrc: string | null
	setNewImageSrc: React.Dispatch<React.SetStateAction<string | null>>
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={[className, 'flex flex-col gap-3'].join(' ')}>
			{label ? (
				<div className="flex items-center justify-between">
					<Label htmlFor={id} {...labelProps} />
					{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
				</div>
			) : null}
			<Button className="w-fit p-0" size="s" type="button">
				<label
					className="flex h-full cursor-pointer items-center justify-center px-[12px]"
					htmlFor={id}
				>
					Upload
				</label>
			</Button>
			<input
				{...inputProps}
				id={id}
				type="file"
				accept="image/*"
				className="peer sr-only"
				required
				tabIndex={newImageSrc ? -1 : 0}
				onChange={e => {
					const file = e.currentTarget.files?.[0]
					if (file) {
						const reader = new FileReader()
						reader.onload = event => {
							setNewImageSrc(event.target?.result?.toString() ?? null)
						}
						reader.readAsDataURL(file)
					}
				}}
			/>
		</div>
	)
}

export function SwitchField({
	labelProps,
	switchProps,
	errors,
	className,
	defaultChecked = false,
	onChange,
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	switchProps?: Omit<Switch.SwitchProps, 'checked' | 'onCheckedChange'> & {
		name: string
		form: string
	}
	errors?: ListOfErrors
	className?: string
	defaultChecked?: boolean
	onChange?: (checked: boolean) => void
}) {
	const [checked, setChecked] = useState(defaultChecked)
	const fallbackId = useId()
	const id = switchProps?.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex items-center gap-2">
				<Label
					htmlFor={id}
					{...labelProps}
					className="w-full self-center text-body-xs"
				/>
				<Switch.Root
					{...switchProps}
					id={id}
					checked={checked}
					onCheckedChange={newChecked => {
						setChecked(newChecked)
						onChange?.(newChecked)
					}}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					className="peer h-[24px] w-[44px] shrink-0 cursor-pointer rounded-full border bg-primary-foreground bg-gradient-to-b from-light-start-focus to-light-end-focus  transition-colors duration-200 hover:border-neutral-300 data-[state=checked]:bg-gradient-to-b data-[state=checked]:from-dark-start data-[state=checked]:to-dark-end"
				>
					<Switch.Thumb className="block h-[20px] w-[20px] translate-x-[1.5px] rounded-full border bg-background bg-gradient-to-b from-light-start to-light-end transition-transform duration-200 will-change-transform hover:bg-gradient-to-b hover:from-light-start-hover hover:to-light-end-hover hover:pb-[1px] active:bg-gradient-to-b active:from-light-start-focus active:to-light-end-focus active:pt-1 data-[state=checked]:translate-x-[21px]" />
				</Switch.Root>
			</div>
		</div>
	)
}
